import {
  AfterViewInit,
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { debounceTime, distinctUntilChanged, filter, Subject, takeUntil } from 'rxjs';
import { SearchResultsService } from 'src/app/pages/start-page/services/search-results.service';
import { PlatformService } from '../../services/platform-service/platform.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationStart, Router } from '@angular/router';

@Component({
  selector: 'search-bar',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule],
  templateUrl: './search-bar.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./search-bar.component.scss'],
})
export class SearchBarComponent implements OnDestroy, OnInit, AfterViewInit {
  @Input() placeholder = 'Search';
  @Input() isCollapsible: boolean = false;
  @Input() isGrayBackground: boolean = true;
  @Input() isLightGrayBackground: boolean = true;
  @Input() noBackgroundWithBorder: boolean = false;
  @Input() isScreenStartPage: boolean = false;
  /**
   * FormControl allows us to use listen to changes on the value of the fields.
   * In this example:
   * query is a FormControl which is used in the html-input and we can subscribe to any change to its value.
   * This is done in the constructor be using the query.valueChanges.subscribe-method.
   */
  @Input() query: FormControl = new FormControl('');
  @Output() queryChanged: EventEmitter<string> = new EventEmitter<string>();
  @Output() focusChanged: EventEmitter<boolean> = new EventEmitter<boolean>();

  @ViewChild('inputElement', { static: false }) inputElement!: ElementRef;

  @HostListener('window:scroll', [])
  onWindowScroll(): void {
    if (!this.inputElement) return;
    const currentScrollPosition =
      window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0;
    this.positionScrollChange.next(currentScrollPosition);
  }

  @HostListener('document:touchmove', ['$event'])
  onDocumentTouchMove(_event: TouchEvent): void {
    if (!this.platformService.isMobile() || !this.inputElement) {
      return;
    }

    const active = document.activeElement as HTMLElement | null;
    if (!active) return;

    if (active === this.inputElement.nativeElement) {
      this.inputElement.nativeElement.blur();
      this.focusout();

      try {
        const y = window.pageYOffset || document.documentElement.scrollTop || 0;
        window.scrollTo(0, y + 1);
        window.scrollTo(0, y);
      } catch {}
    }
  }

  lastScrollPosition = 0;
  showInput: boolean = false;
  showInputSearch: boolean = true;
  isAlive$: Subject<boolean> = new Subject();
  positionScrollChange = new Subject<number>();
  ignoreScroll = false;

  constructor(
    protected searchResultsService: SearchResultsService,
    public platformService: PlatformService,
    private readonly destroyRef: DestroyRef,
    private router: Router,
  ) {}

  ngOnInit(): void {
    this.query.valueChanges
      .pipe(takeUntil(this.isAlive$), debounceTime(500), distinctUntilChanged())
      .subscribe((_newValue) => {
        this.queryChanged.emit(_newValue);

        if (_newValue.length > 2) {
          this.searchResultsService.shouldOpenSearch.next(true);
        } else {
          this.searchResultsService.shouldOpenSearch.next(false);
        }
      });

    // Unfocus the currently active element (like input) on every route change
    this.router.events
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter((event) => event instanceof NavigationStart),
      )
      .subscribe((event: NavigationStart) => {
        // Only unfocus / hide search if the route path changes, not query params
        const currentPath = this.router.url.split('?')[0];
        const nextPath = event.url.split('?')[0];

        if (currentPath !== nextPath) {
          this.blurInput();
          this.searchResultsService.shouldOpenSearch.next(false);
        }
      });

    if (this.isScreenStartPage && this.platformService.isMobile()) {
      this.searchResultsService.isSearchResultVisible$
        .pipe(takeUntil(this.isAlive$))
        .subscribe((_searchResultVisible) => {
          if (!_searchResultVisible) {
            this.clearSearchBar();
          }
        });
    }

    this.unfocusInputWhenscroll();
  }

  private blurInput() {
    if (this.inputElement) {
      this.inputElement.nativeElement.blur();
    }
  }

  ngAfterViewInit(): void {
    if (!this.platformService.isMobile()) {
      setTimeout(() => {
        this.inputElement.nativeElement.focus();
      }, 1000);
    }
  }

  ngOnDestroy(): void {
    this.isAlive$.next(false);
    this.isAlive$.complete();
  }

  unfocusInputWhenscroll() {
    this.positionScrollChange
      .pipe(debounceTime(200), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (currentScrollPosition) => {
          if (this.ignoreScroll) return;

          const delta = Math.abs(currentScrollPosition - this.lastScrollPosition);

          this.lastScrollPosition = currentScrollPosition;

          if (this.platformService.isMobile() && delta > 30) {
            this.inputElement.nativeElement.blur();
            this.focusout();
          }
        },
      });
  }

  focusout() {
    if (
      this.isCollapsible &&
      this.isScreenStartPage &&
      this.platformService.isMobile() &&
      this.query.value.length === 0
    ) {
      this.showInput = false;
    }

    setTimeout(() => {
      this.searchResultsService.shouldOpenSearch.next(false);
      this.focusChanged.emit(false);
    }, 200);
  }

  focusIn() {
    this.ignoreScroll = true;
    setTimeout(() => (this.ignoreScroll = false), 1000);

    if (this.query.value.length > 2) {
      this.searchResultsService.shouldOpenSearch.next(true);
    }
    this.focusChanged.emit(true);
  }

  focusInButton() {
    this.showInput = true;
    this.inputElement.nativeElement.focus();
  }

  clearSearchBar() {
    this.query.setValue('');
    this.searchResultsService.shouldOpenSearch.next(false);

    if (!this.platformService.isMobile()) {
      setTimeout(() => {
        this.inputElement.nativeElement.focus();
      }, 200);
    }
  }

  onEnter(event: Event) {
    event.preventDefault();
    this.inputElement.nativeElement.blur();
    this.focusout();
  }

  onSubmit(event: Event) {
    event.preventDefault();
    this.inputElement.nativeElement.blur();
    this.focusout();
  }
}
